var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import classNames from 'classnames';
import { NotificationProvider, useNotification as useRcNotification } from 'rc-notification';
import { devUseWarning } from '../_util/warning';
import { ConfigContext } from '../config-provider';
import { PureContent } from './PurePanel';
import useStyle from './style';
import { getMotion, wrapPromiseFn } from './util';
const DEFAULT_OFFSET = 8;
const DEFAULT_DURATION = 3;
const Wrapper = ({ children, prefixCls }) => {
    const [wrapCSSVar, hashId] = useStyle(prefixCls);
    return wrapCSSVar(React.createElement(NotificationProvider, { classNames: { list: hashId, notice: hashId } }, children));
};
const renderNotifications = (node, { prefixCls, key }) => (React.createElement(Wrapper, { prefixCls: prefixCls, key: key }, node));
const Holder = React.forwardRef((props, ref) => {
    const { top, prefixCls: staticPrefixCls, getContainer: staticGetContainer, maxCount, duration = DEFAULT_DURATION, rtl, transitionName, onAllRemoved, } = props;
    const { getPrefixCls, getPopupContainer, message } = React.useContext(ConfigContext);
    const prefixCls = staticPrefixCls || getPrefixCls('message');
    // =============================== Style ===============================
    const getStyle = () => ({
        left: '50%',
        transform: 'translateX(-50%)',
        top: top !== null && top !== void 0 ? top : DEFAULT_OFFSET,
    });
    const getClassName = () => classNames({ [`${prefixCls}-rtl`]: rtl });
    // ============================== Motion ===============================
    const getNotificationMotion = () => getMotion(prefixCls, transitionName);
    // ============================ Close Icon =============================
    const mergedCloseIcon = (React.createElement("span", { className: `${prefixCls}-close-x` },
        React.createElement(CloseOutlined, { className: `${prefixCls}-close-icon` })));
    // ============================== Origin ===============================
    const [api, holder] = useRcNotification({
        prefixCls,
        style: getStyle,
        className: getClassName,
        motion: getNotificationMotion,
        closable: false,
        closeIcon: mergedCloseIcon,
        duration,
        getContainer: () => (staticGetContainer === null || staticGetContainer === void 0 ? void 0 : staticGetContainer()) || (getPopupContainer === null || getPopupContainer === void 0 ? void 0 : getPopupContainer()) || document.body,
        maxCount,
        onAllRemoved,
        renderNotifications,
    });
    // ================================ Ref ================================
    React.useImperativeHandle(ref, () => (Object.assign(Object.assign({}, api), { prefixCls,
        message })));
    return holder;
});
// ==============================================================================
// ==                                   Hook                                   ==
// ==============================================================================
let keyIndex = 0;
export function useInternalMessage(messageConfig) {
    const holderRef = React.useRef(null);
    const warning = devUseWarning('Message');
    // ================================ API ================================
    const wrapAPI = React.useMemo(() => {
        // Wrap with notification content
        // >>> close
        const close = (key) => {
            var _a;
            (_a = holderRef.current) === null || _a === void 0 ? void 0 : _a.close(key);
        };
        // >>> Open
        const open = (config) => {
            if (!holderRef.current) {
                warning(false, 'usage', 'You are calling notice in render which will break in React 18 concurrent mode. Please trigger in effect instead.');
                const fakeResult = () => { };
                fakeResult.then = () => { };
                return fakeResult;
            }
            const { open: originOpen, prefixCls, message } = holderRef.current;
            const noticePrefixCls = `${prefixCls}-notice`;
            const { content, icon, type, key, className, style, onClose } = config, restConfig = __rest(config, ["content", "icon", "type", "key", "className", "style", "onClose"]);
            let mergedKey = key;
            if (mergedKey === undefined || mergedKey === null) {
                keyIndex += 1;
                mergedKey = `antd-message-${keyIndex}`;
            }
            return wrapPromiseFn((resolve) => {
                originOpen(Object.assign(Object.assign({}, restConfig), { key: mergedKey, content: (React.createElement(PureContent, { prefixCls: prefixCls, type: type, icon: icon }, content)), placement: 'top', className: classNames(type && `${noticePrefixCls}-${type}`, className, message === null || message === void 0 ? void 0 : message.className), style: Object.assign(Object.assign({}, message === null || message === void 0 ? void 0 : message.style), style), onClose: () => {
                        onClose === null || onClose === void 0 ? void 0 : onClose();
                        resolve();
                    } }));
                // Return close function
                return () => {
                    close(mergedKey);
                };
            });
        };
        // >>> destroy
        const destroy = (key) => {
            var _a;
            if (key !== undefined) {
                close(key);
            }
            else {
                (_a = holderRef.current) === null || _a === void 0 ? void 0 : _a.destroy();
            }
        };
        const clone = {
            open,
            destroy,
        };
        const keys = ['info', 'success', 'warning', 'error', 'loading'];
        keys.forEach((type) => {
            const typeOpen = (jointContent, duration, onClose) => {
                let config;
                if (jointContent && typeof jointContent === 'object' && 'content' in jointContent) {
                    config = jointContent;
                }
                else {
                    config = {
                        content: jointContent,
                    };
                }
                // Params
                let mergedDuration;
                let mergedOnClose;
                if (typeof duration === 'function') {
                    mergedOnClose = duration;
                }
                else {
                    mergedDuration = duration;
                    mergedOnClose = onClose;
                }
                const mergedConfig = Object.assign(Object.assign({ onClose: mergedOnClose, duration: mergedDuration }, config), { type });
                return open(mergedConfig);
            };
            clone[type] = typeOpen;
        });
        return clone;
    }, []);
    // ============================== Return ===============================
    return [wrapAPI, React.createElement(Holder, Object.assign({ key: "message-holder" }, messageConfig, { ref: holderRef }))];
}
export default function useMessage(messageConfig) {
    return useInternalMessage(messageConfig);
}
